import { ChangeDetectionStrategy, Component, effect, signal, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";
import { SystemActionComponent } from "./abstract.system.action.component";
import { Item } from "../../../pojo/Item";
import { WorkflowCustomAttributeMappingsComponent } from "./workflow.custom.attribute.mappings.component";
import {
    parseWorkflowCustAtrbMappings,
    serializeWorkflowCustAtrbMappings,
    WorkflowCustAtrbMappingUiRow,
} from "./workflow.cust.atrb.mappings.util";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-add-custom-attributes-action",
    template: `
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventIdField']?.inputValue || ''"
            (valueChange)="setInputValue('eventIdField', $event)"
            [label]="'Event ID Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Map a form field containing the event ID. If the form creates an event, the event ID is automatically available so there is no need to map it.'
            "
            [acceptedAnswerTypes]="acceptedFor('eventIdField')"
        />

        <div class="form-group">
            <small class="form-text">
                Custom attribute values are automatically collected from all CUSTOM_ATTRIBUTE form fields in the
                submission. You can also map additional custom attributes manually below.
            </small>
            <label class="checkbox-option">
                <input
                    type="checkbox"
                    [checked]="useManualMappings()"
                    (change)="onToggleManualMappings($any($event.target).checked)"
                />
                Map custom attributes manually
            </label>
        </div>

        @if (useManualMappings()) {
            <s25-ng-workflow-custom-attribute-mappings
                [itemTypeId]="itemIds.Event"
                [mappings]="mappings()"
                (mappingsChange)="onMappingsChange($event)"
            />
        }
    `,
    styles: `
        .form-group {
            margin-bottom: 16px;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 8px;
            display: block;
        }

        .checkbox-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            font-weight: normal;
        }

        .checkbox-option input[type="checkbox"] {
            margin: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, WorkflowActionInputComponent, WorkflowCustomAttributeMappingsComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCustomAttributesActionComponent extends SystemActionComponent {
    protected readonly actionCode = "ADD_CUSTOM_ATTRIBUTES" as const;
    readonly itemIds = Item.Ids;
    readonly useManualMappings = signal(false);
    readonly mappings = signal<WorkflowCustAtrbMappingUiRow[]>([]);
    private initialized = false;

    constructor() {
        super();

        effect(() => {
            const inputs = this.inputMap();
            if (this.initialized) return;
            this.initialized = true;
            this.initializeFromInputs(inputs);
        });
    }

    private initializeFromInputs(inputs: Record<string, { inputValue?: string } | undefined>) {
        const parsed = parseWorkflowCustAtrbMappings(inputs["customAttributes"]?.inputValue);
        if (parsed.length) {
            this.useManualMappings.set(true);
            this.mappings.set(parsed);
        }
    }

    onToggleManualMappings(checked: boolean) {
        this.useManualMappings.set(checked);
        if (!checked) {
            this.mappings.set([]);
            this.setInputValue("customAttributes", "");
        }
    }

    onMappingsChange(rows: WorkflowCustAtrbMappingUiRow[]): void {
        this.mappings.set(rows);
        this.setInputValue("customAttributes", serializeWorkflowCustAtrbMappings(rows));
    }
}
